import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const CREW_ROLES = ["Cinematographer", "Photographer", "Editor", "DOP", "Director", "Drone Pilot"];

const emptyProject = {
  name: "", client_id: "", budget: 0, quotation: 0, invoice_amount: 0, amount_paid: 0, payment_status: "unpaid",
  shoot_date: "", shoot_end_date: "", delivery_date: "", location: "", call_time: "", status: "planning",
  description: "", crew: [], expenses: [], deliverables: [],
};

export default function ProjectDialog({ open, onOpenChange, onSaved, existing }) {
  const [form, setForm] = useState(emptyProject);
  const [clients, setClients] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [tab, setTab] = useState("details");

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then(r => setClients(r.data));
    api.get("/freelancers").then(r => setFreelancers(r.data));
    setForm(existing ? { ...emptyProject, ...existing } : emptyProject);
    setTab("details");
  }, [open, existing]);

  useEffect(() => {
    if (!form.crew?.length || !form.shoot_date) { setConflicts([]); return; }
    api.post("/projects/check-conflict", {
      freelancer_ids: form.crew.map(c => c.freelancer_id).filter(Boolean),
      shoot_date: form.shoot_date, shoot_end_date: form.shoot_end_date,
      exclude_project_id: existing?.id,
    }).then(r => setConflicts(r.data.conflicts)).catch(() => {});
  }, [form.crew, form.shoot_date, form.shoot_end_date, existing?.id]);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const num = (v) => Number(v) || 0;

  const addCrew = () => upd("crew", [...form.crew, { freelancer_id: "", role: "Cinematographer", payout: 0, travel: 0, equipment_rental: 0 }]);
  const rmCrew = (i) => upd("crew", form.crew.filter((_, idx) => idx !== i));
  const updCrew = (i, k, v) => upd("crew", form.crew.map((c, idx) => idx === i ? { ...c, [k]: k === "freelancer_id" || k === "role" ? v : num(v) } : c));

  const addExp = () => upd("expenses", [...form.expenses, { category: "misc", description: "", amount: 0, date: new Date().toISOString() }]);
  const rmExp = (i) => upd("expenses", form.expenses.filter((_, idx) => idx !== i));
  const updExp = (i, k, v) => upd("expenses", form.expenses.map((e, idx) => idx === i ? { ...e, [k]: k === "amount" ? num(v) : v } : e));

  const addDel = () => upd("deliverables", [...form.deliverables, { name: "", type: "reel", editor_id: "", deadline: "", status: "pending" }]);
  const rmDel = (i) => upd("deliverables", form.deliverables.filter((_, idx) => idx !== i));
  const updDel = (i, k, v) => upd("deliverables", form.deliverables.map((d, idx) => idx === i ? { ...d, [k]: v } : d));

  const crewCost = form.crew.reduce((s, c) => s + num(c.payout) + num(c.travel) + num(c.equipment_rental), 0);
  const expTotal = form.expenses.reduce((s, e) => s + num(e.amount), 0);
  const totalCost = crewCost + expTotal;
  const profit = num(form.invoice_amount) - totalCost;

  const save = async () => {
    if (!form.name || !form.client_id) return toast.error("Name and client required");
    try {
      const payload = { ...form, budget: num(form.budget), quotation: num(form.quotation), invoice_amount: num(form.invoice_amount), amount_paid: num(form.amount_paid) };
      if (existing) await api.put(`/projects/${existing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(existing ? "Project updated" : "Project created");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const inp = "w-full bg-transparent border border-white/10 rounded-md px-2.5 py-1.5 text-sm focus:border-white/30 focus:outline-none";
  const lbl = "text-[10px] uppercase tracking-widest text-white/40";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{existing ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>

        <div className="flex gap-1 border-b border-white/10 pb-2 -mx-6 px-6">
          {["details", "crew", "expenses", "deliverables"].map(t => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`text-xs px-3 py-1.5 rounded-md capitalize ${tab === t ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}>{t}</button>
          ))}
        </div>

        {tab === "details" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className={lbl}>Project name*</label><input data-testid="project-name" value={form.name} onChange={e => upd("name", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Client*</label>
              <select data-testid="project-client" value={form.client_id} onChange={e => upd("client_id", e.target.value)} className={inp}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Status</label>
              <select value={form.status} onChange={e => upd("status", e.target.value)} className={inp}>
                {["planning", "active", "shooting", "editing", "completed", "cancelled"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Shoot Date</label><input type="date" value={form.shoot_date?.slice(0, 10) || ""} onChange={e => upd("shoot_date", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Shoot End Date</label><input type="date" value={form.shoot_end_date?.slice(0, 10) || ""} onChange={e => upd("shoot_end_date", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Delivery Date</label><input type="date" value={form.delivery_date?.slice(0, 10) || ""} onChange={e => upd("delivery_date", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Call Time</label><input value={form.call_time || ""} onChange={e => upd("call_time", e.target.value)} className={inp} placeholder="06:30 AM" /></div>
            <div className="col-span-2"><label className={lbl}>Location</label><input value={form.location || ""} onChange={e => upd("location", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Budget</label><input type="number" value={form.budget} onChange={e => upd("budget", e.target.value)} className={inp + " mono"} /></div>
            <div><label className={lbl}>Quotation</label><input type="number" value={form.quotation} onChange={e => upd("quotation", e.target.value)} className={inp + " mono"} /></div>
            <div><label className={lbl}>Invoice Amount</label><input type="number" value={form.invoice_amount} onChange={e => upd("invoice_amount", e.target.value)} className={inp + " mono"} /></div>
            <div><label className={lbl}>Amount Paid</label><input type="number" value={form.amount_paid} onChange={e => upd("amount_paid", e.target.value)} className={inp + " mono"} /></div>
            <div><label className={lbl}>Payment Status</label>
              <select value={form.payment_status} onChange={e => upd("payment_status", e.target.value)} className={inp}>
                {["unpaid", "partial", "paid"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className={lbl}>Description</label><textarea rows={2} value={form.description || ""} onChange={e => upd("description", e.target.value)} className={inp} /></div>
          </div>
        )}

        {tab === "crew" && (
          <div className="space-y-3">
            {conflicts.length > 0 && (
              <div className="hairline rounded-md p-3 bg-amber-500/5 border-amber-500/30 text-xs">
                <div className="flex items-center gap-2 font-semibold text-amber-400 mb-1"><Warning size={14} weight="fill" /> Schedule conflict detected</div>
                {conflicts.map((c, i) => {
                  const f = freelancers.find(x => x.id === c.freelancer_id);
                  return <div key={i} className="text-white/70 ml-6">• <span className="font-medium">{f?.name}</span> is already booked on <span className="mono">{c.project_name}</span> ({c.shoot_date?.slice(0, 10)})</div>;
                })}
              </div>
            )}
            {form.crew.map((c, i) => (
              <div key={i} data-testid={`crew-row-${i}`} className="hairline rounded-md p-3 grid grid-cols-12 gap-2 items-end bg-white/[0.02]">
                <div className="col-span-4"><label className={lbl}>Freelancer</label>
                  <select value={c.freelancer_id} onChange={e => updCrew(i, "freelancer_id", e.target.value)} className={inp}>
                    <option value="">Select…</option>
                    {freelancers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="col-span-3"><label className={lbl}>Role</label>
                  <select data-testid={`crew-role-${i}`} value={c.role} onChange={e => updCrew(i, "role", e.target.value)} className={inp}>
                    {CREW_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-span-1"><label className={lbl}>Payout</label><input type="number" value={c.payout} onChange={e => updCrew(i, "payout", e.target.value)} className={inp + " mono"} /></div>
                <div className="col-span-1"><label className={lbl}>Travel</label><input type="number" value={c.travel} onChange={e => updCrew(i, "travel", e.target.value)} className={inp + " mono"} /></div>
                <div className="col-span-2"><label className={lbl}>Equipment</label><input type="number" value={c.equipment_rental} onChange={e => updCrew(i, "equipment_rental", e.target.value)} className={inp + " mono"} /></div>
                <button data-testid={`remove-crew-${i}`} onClick={() => rmCrew(i)} className="col-span-1 text-white/40 hover:text-red-400 py-2"><Trash size={14} /></button>
              </div>
            ))}
            <button data-testid="add-crew-btn" onClick={addCrew} className="text-xs text-white/60 hover:text-white flex items-center gap-1 border border-dashed border-white/10 rounded-md w-full py-2 justify-center hover:bg-white/[0.02]">
              <Plus size={14} /> Add crew member
            </button>
            <div className="text-xs text-white/60 border-t border-white/10 pt-3 flex justify-between mono">
              <span>Total crew cost</span><span className="text-white font-bold">₹{crewCost.toLocaleString("en-IN")}</span>
            </div>
          </div>
        )}

        {tab === "expenses" && (
          <div className="space-y-3">
            {form.expenses.map((e, i) => (
              <div key={i} className="hairline rounded-md p-3 grid grid-cols-12 gap-2 items-end bg-white/[0.02]">
                <div className="col-span-3"><label className={lbl}>Category</label>
                  <select value={e.category} onChange={ev => updExp(i, "category", ev.target.value)} className={inp}>
                    {["travel", "food", "fuel", "hotel", "rental", "misc"].map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
                <div className="col-span-6"><label className={lbl}>Description</label><input value={e.description} onChange={ev => updExp(i, "description", ev.target.value)} className={inp} /></div>
                <div className="col-span-2"><label className={lbl}>Amount</label><input type="number" value={e.amount} onChange={ev => updExp(i, "amount", ev.target.value)} className={inp + " mono"} /></div>
                <button onClick={() => rmExp(i)} className="col-span-1 text-white/40 hover:text-red-400 py-2"><Trash size={14} /></button>
              </div>
            ))}
            <button data-testid="add-expense-btn" onClick={addExp} className="text-xs text-white/60 hover:text-white flex items-center gap-1 border border-dashed border-white/10 rounded-md w-full py-2 justify-center hover:bg-white/[0.02]">
              <Plus size={14} /> Add expense
            </button>
            <div className="mono text-xs border-t border-white/10 pt-3 space-y-1">
              <div className="flex justify-between text-white/60"><span>Crew cost</span><span>₹{crewCost.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-white/60"><span>Expenses</span><span>₹{expTotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-white font-bold"><span>Total cost</span><span>₹{totalCost.toLocaleString("en-IN")}</span></div>
              <div className={`flex justify-between font-bold ${profit >= 0 ? "text-emerald-400" : "text-red-400"}`}><span>Projected profit</span><span>₹{profit.toLocaleString("en-IN")}</span></div>
            </div>
          </div>
        )}

        {tab === "deliverables" && (
          <div className="space-y-3">
            {form.deliverables.map((d, i) => (
              <div key={i} className="hairline rounded-md p-3 grid grid-cols-12 gap-2 items-end bg-white/[0.02]">
                <div className="col-span-4"><label className={lbl}>Name</label><input value={d.name} onChange={e => updDel(i, "name", e.target.value)} className={inp} /></div>
                <div className="col-span-2"><label className={lbl}>Type</label>
                  <select value={d.type} onChange={e => updDel(i, "type", e.target.value)} className={inp}>
                    {["reel", "photo", "video"].map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
                <div className="col-span-3"><label className={lbl}>Editor</label>
                  <select value={d.editor_id || ""} onChange={e => updDel(i, "editor_id", e.target.value)} className={inp}>
                    <option value="">—</option>
                    {freelancers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><label className={lbl}>Deadline</label><input type="date" value={d.deadline?.slice(0, 10) || ""} onChange={e => updDel(i, "deadline", e.target.value)} className={inp} /></div>
                <button onClick={() => rmDel(i)} className="col-span-1 text-white/40 hover:text-red-400 py-2"><Trash size={14} /></button>
                <div className="col-span-12"><select value={d.status} onChange={e => updDel(i, "status", e.target.value)} className={inp}>
                  {["pending", "in_progress", "review", "done"].map(x => <option key={x}>{x}</option>)}
                </select></div>
              </div>
            ))}
            <button data-testid="add-deliverable-btn" onClick={addDel} className="text-xs text-white/60 hover:text-white flex items-center gap-1 border border-dashed border-white/10 rounded-md w-full py-2 justify-center hover:bg-white/[0.02]">
              <Plus size={14} /> Add deliverable
            </button>
          </div>
        )}

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="text-xs text-white/60 hover:text-white px-3 py-2">Cancel</button>
          <button data-testid="save-project-btn" onClick={save} className="bg-white text-black text-xs font-semibold px-4 py-2 rounded-md hover:bg-white/90">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
